import { faCardsBlank } from "@fortawesome/duotone-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  FlexBox,
  JoonUIColor,
  QuestStatus,
  SPACING,
  Typography,
  QuestInstance,
  QuestRoutine,
  SegmentedControl2,
  DropdownOption,
  UserRole,
  Button,
  TextButton,
} from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"
import { useShallow } from "zustand/react/shallow"

import AddAIQuestButton from "./AddAIQuestButton"
import { ROUTINE_LABEL_KEYS } from "./addEditQuestModal/questLabelKeys"
import { useAddQuestTemplatesModalStore } from "./addQuestTemplates/useAddQuestTemplatesModalStore"
import { useAllQuestsModalStore } from "./allQuests/hooks/useAllQuestsModalStore"
import QuestBoardDatePicker, {
  useQBDatePickerStore,
} from "./QuestBoardDatePicker"
import QuestCard from "./QuestCard"
import { QuestGroupCard } from "./QuestGroupCard"
import QuestRoutineSection from "./QuestRoutineSection"
import useQuestBoardFilters from "./useQuestBoardFilters"
import useQuestBoardMultiSelectStore from "./useQuestBoardMultiSelectStore"
import useQuestBoardStore from "./useQuestBoardStore"
import DiamondRow from "../../components/diamondRow/DiamondRow"
import { QuestBoardSkeleton } from "../../components/skeleton/Skeleton"
import { parentQuestItems, questRoutineOptions } from "../../constants"
import { useMissionsQuery } from "../../networking/missions/queries"
import { useFamilyQuery, useQuestBoardQuery } from "../../networking/queries"
import { MissionScene, MissionStatus } from "../../types"
import { QuestSectionType } from "../../types/quests"
import { ANALYTIC_EVENTS, trackAnalyticEvent } from "../../util/analytics"
import { sortQuestSection } from "../../util/util"

// Translation keys for parentQuestItems (constants holds the icons), aligned by index.
const PARENT_QUEST_ITEM_KEYS = [
  { title: "QB_PARENT_ITEM1_TITLE", description: "QB_PARENT_ITEM1_DESC" },
  { title: "QB_PARENT_ITEM2_TITLE", description: "QB_PARENT_ITEM2_DESC" },
  { title: "QB_PARENT_ITEM3_TITLE", description: "QB_PARENT_ITEM3_DESC" },
] as const

const AssignedQuests = () => {
  const { t } = useTranslation()
  const { questBoardDate } = useQBDatePickerStore()
  const { filteredQuests } = useQuestBoardFilters()
  const { isLoading: isLoadingQuests } = useQuestBoardQuery()
  const { selectedRoutine, setSelectedRoutine } = useQuestBoardStore()
  const { onOpen: onOpenAllQuestsModal } = useAllQuestsModalStore()
  const { isMultiSelectMode } = useQuestBoardMultiSelectStore(
    useShallow((state) => ({
      isMultiSelectMode: state.isMultiSelectMode,
      selectedQuestIds: state.selectedQuestIds,
    }))
  )

  const { data: missions } = useMissionsQuery()

  const needsToAddFirstQuest = !!missions?.some(
    (mission) =>
      mission.status.status === MissionStatus.INCOMPLETE &&
      mission.scene_name === MissionScene.QUEST_TAB
  )

  const {
    completedQuestSection,
    routineSections,
    inReviewQuestSection,
    rejectedQuestSection,
    skippedQuestSection,
    selfInitiatedQuestSection,
  } = createQuestSections(filteredQuests)

  const handleOpenAllQuestsModal = () => {
    trackAnalyticEvent(ANALYTIC_EVENTS.REORDER_QUESTS)
    setSelectedRoutine(null)
    onOpenAllQuestsModal()
  }

  return (
    <div>
      {!isMultiSelectMode && (
        <>
          <FlexBox
            align="flex-end"
            justify="space-between"
            wrap={false}
            style={{ marginBottom: SPACING.space1, marginTop: SPACING.space2 }}
          >
            <Typography variant="bodyBold">
              {questBoardDate.format("MMMM YYYY")}
            </Typography>
            <TextButton onClick={handleOpenAllQuestsModal}>
              <FlexBox
                direction="row"
                align="center"
                gap={SPACING.space1}
                wrap={false}
              >
                <FontAwesomeIcon
                  icon={faCardsBlank}
                  size="xs"
                  color={JoonUIColor.text.primary}
                />
                <Typography variant="bodyXSmallBold">
                  {t("QB_ALL_QUESTS")}
                </Typography>
              </FlexBox>
            </TextButton>
          </FlexBox>
          <QuestBoardDatePicker />
          <SegmentedControl2
            options={questRoutineOptions.map((tab) => ({
              ...tab,
              label:
                tab.value === null
                  ? t("QB_ROUTINE_ALL")
                  : t(ROUTINE_LABEL_KEYS[tab.value as QuestRoutine]),
            }))}
            value={
              {
                ...(questRoutineOptions.find(
                  (tab) => tab.value === selectedRoutine
                ) as DropdownOption),
                label:
                  selectedRoutine === null
                    ? t("QB_ROUTINE_ALL")
                    : t(ROUTINE_LABEL_KEYS[selectedRoutine as QuestRoutine]),
              } as DropdownOption
            }
            setValue={(option) =>
              setSelectedRoutine(option.value as QuestRoutine)
            }
            fullWidth
            style={{
              color: JoonUIColor.text.primaryAccent,
              margin: `${SPACING.space3} 0`,
              border: `1px solid ${JoonUIColor.border.default}`,
            }}
          />
        </>
      )}
      {isLoadingQuests ? (
        <QuestBoardSkeleton />
      ) : filteredQuests?.length === 0 || needsToAddFirstQuest ? (
        <QuestBoardEmptyDay />
      ) : (
        <FlexBox
          direction="column"
          gap={SPACING.space4}
          style={{ marginTop: SPACING.space4, paddingBottom: "160px" }}
        >
          {routineSections.map(([sectionTitle, quests], i) => (
            <QuestRoutineSection
              key={i}
              title={sectionTitle}
              questInstances={quests}
              icon={`/images/icons/${sectionTitle}-routine-icon.png`}
            />
          ))}

          {selfInitiatedQuestSection.length > 0 && (
            <div className="w-full flex flex-col gap-1">
              <div className="flex flex-col items-center">
                <div className="flex flex-row gap-1">
                  <img
                    src="/images/icons/shooting-star.svg"
                    alt="Self-initiated"
                    style={{ height: "18px" }}
                  />
                  <Typography variant="bodySmallBold">
                    {t("QB_SELF_INITIATED_TITLE")}
                  </Typography>
                </div>
                <Typography variant="bodySmall" textAlign="center">
                  {t("QB_SELF_INITIATED_SUB")}
                </Typography>
              </div>

              {selfInitiatedQuestSection.map((questInstances, i) => (
                <QuestCard
                  questInstance={questInstances[0]}
                  type={QuestSectionType.SELF_INITIATED}
                  index={i}
                  key={i}
                />
              ))}
            </div>
          )}

          {inReviewQuestSection.length > 0 && (
            <FlexBox direction="column" gap={SPACING.space1}>
              {inReviewQuestSection.map((questInstances, i) =>
                questInstances.length === 1 ? (
                  <QuestCard
                    questInstance={questInstances[0]}
                    type={QuestSectionType.REVIEW}
                    index={i}
                    key={i}
                  />
                ) : (
                  <QuestGroupCard
                    questGroup={questInstances}
                    index={i}
                    type={QuestSectionType.REVIEW}
                  />
                )
              )}
            </FlexBox>
          )}
          {completedQuestSection.length > 0 && (
            <FlexBox direction="column" gap={SPACING.space1}>
              {completedQuestSection.map((questInstances, i) =>
                questInstances.length === 1 ? (
                  <QuestCard
                    questInstance={questInstances[0]}
                    type={QuestSectionType.COMPLETED}
                    key={i}
                    index={i}
                  />
                ) : (
                  <QuestGroupCard
                    questGroup={questInstances}
                    index={i}
                    type={QuestSectionType.COMPLETED}
                  />
                )
              )}
            </FlexBox>
          )}
          {skippedQuestSection.length > 0 && (
            <FlexBox direction="column" gap={SPACING.space1}>
              {skippedQuestSection.map((questInstances, i) =>
                questInstances.length === 1 ? (
                  <QuestCard
                    questInstance={questInstances[0]}
                    type={QuestSectionType.SKIPPED}
                    key={i}
                    index={i}
                  />
                ) : (
                  <QuestGroupCard
                    questGroup={questInstances}
                    index={i}
                    type={QuestSectionType.SKIPPED}
                  />
                )
              )}
            </FlexBox>
          )}
          {rejectedQuestSection.length > 0 && (
            <FlexBox direction="column" gap={SPACING.space1}>
              {rejectedQuestSection.map((questInstances, i) =>
                questInstances.length === 1 ? (
                  <QuestCard
                    questInstance={questInstances[0]}
                    type={QuestSectionType.REJECTED}
                    key={i}
                    index={i}
                  />
                ) : (
                  <QuestGroupCard
                    questGroup={questInstances}
                    index={i}
                    type={QuestSectionType.REJECTED}
                  />
                )
              )}
            </FlexBox>
          )}
        </FlexBox>
      )}
    </div>
  )
}

export default AssignedQuests

const QuestBoardEmptyDay = () => {
  const { t } = useTranslation()
  const { data } = useQuestBoardQuery()
  const { onOpen: openAddQuestTemplatesModal } =
    useAddQuestTemplatesModalStore()
  const { data: family } = useFamilyQuery()
  const { selectedUserId } = useQuestBoardStore()
  const selectedUser = family?.profiles?.find(
    (profile) => profile.user.id === selectedUserId
  )

  const onOpenTemplatesClick = () => {
    openAddQuestTemplatesModal()
    trackAnalyticEvent(ANALYTIC_EVENTS.EMPTY_STATE_OPEN_TEMPLATES, {
      type: "quest",
    })
  }

  const parentIds =
    family?.profiles
      .filter((profile) => profile.role === UserRole.PARENT)
      .map((profile) => profile.user.id) || []
  const hasNotCreatedParentQuests =
    data?.filter((quest) => parentIds.includes(quest.user_id)).length === 0

  return (
    <div className="flex flex-col items-center justify-center gap-3 mt-3">
      <div className="flex flex-col items-center gap-4 p-6">
        <Typography variant="bodyBold">
          {t("QB_EMPTY_CREATE_TO_START")}
        </Typography>
        <div className="flex flex-row gap-2">
          <AddAIQuestButton />
          <Button
            buttonType="primary"
            onClick={onOpenTemplatesClick}
            text={t("QB_CREATE_MY_OWN")}
          />
        </div>
      </div>
      <DiamondRow />
      {selectedUser?.role === UserRole.PARENT && hasNotCreatedParentQuests ? (
        <div className="flex flex-col items-center gap-4 p-6">
          <Typography variant="bodyBold">{t("QB_PARENTS_JOIN")}</Typography>
          <div className="flex flex-col gap-3 w-full">
            {parentQuestItems.map((item, i) => (
              <div
                className="flex flex-row gap-2 border border-borderPrimary rounded-md p-3 bg-white w-full shadow-md"
                key={i}
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  color={JoonUIColor.icon.accent}
                />
                <FlexBox direction="column" gap={SPACING.space1} wrap={false}>
                  <Typography variant="bodySmallBold">
                    {t(PARENT_QUEST_ITEM_KEYS[i].title)}
                  </Typography>
                  <Typography variant="bodySmall">
                    {t(PARENT_QUEST_ITEM_KEYS[i].description)}
                  </Typography>
                </FlexBox>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="flex flex-col items-center  p-6">
          <Typography variant="bodyBold">{t("QB_ASSIGN_CHORES")}</Typography>
          <img
            src="/images/quests_empty.png"
            alt="Parent Quest FTUX"
            className="w-4/5"
          />
        </div>
      )}
    </div>
  )
}

const createQuestSections = (questList: QuestInstance[]) => {
  const completedQuestSection = [] as QuestInstance[][]
  const skippedQuestSection = [] as QuestInstance[][]
  const inReviewQuestSection = [] as QuestInstance[][]
  const rejectedQuestSection = [] as QuestInstance[][]
  const selfInitiatedQuestSection = [] as QuestInstance[][]
  const routineSections = {} as { [key: string]: QuestInstance[][] }

  questList?.forEach((quest) => {
    let targetSection: QuestInstance[][] | null = null

    switch (quest.status) {
      case QuestStatus.SKIPPED:
        targetSection = skippedQuestSection
        break
      case QuestStatus.COMPLETED:
        targetSection = inReviewQuestSection
        break
      case QuestStatus.VERIFIED:
      case QuestStatus.REDEEMED:
        targetSection = completedQuestSection
        break
      case QuestStatus.REJECTED:
      case QuestStatus.REJECTED_VIEWED:
        targetSection = rejectedQuestSection
        break
      default:
        if (quest.series.assigner_profile === null)
          targetSection = selfInitiatedQuestSection
        break
    }

    if (targetSection !== null) {
      const questGroup =
        quest.series.quest_group_id !== null
          ? targetSection.find(
              (questInst) =>
                questInst[0].series.quest_group_id ===
                quest.series.quest_group_id
            )
          : undefined
      if (!!questGroup) {
        questGroup.push(quest)
      } else {
        targetSection.push([quest])
      }
    } else if (quest.series.assigner_profile !== null) {
      if (!routineSections[quest.series.routine])
        routineSections[quest.series.routine] = []
      const questGroup =
        quest.series.quest_group_id !== null
          ? routineSections[quest.series.routine].find(
              (questInst) =>
                questInst[0].series.quest_group_id ===
                quest.series.quest_group_id
            )
          : undefined
      if (!!questGroup) {
        questGroup.push(quest)
      } else {
        routineSections[quest.series.routine].push([quest])
      }
    }
  })

  return {
    completedQuestSection: sortQuestSection(completedQuestSection),
    skippedQuestSection: sortQuestSection(skippedQuestSection),
    inReviewQuestSection: sortQuestSection(inReviewQuestSection),
    rejectedQuestSection: sortQuestSection(rejectedQuestSection),
    routineSections: sortQuestSections(routineSections),
    selfInitiatedQuestSection: sortQuestSection(selfInitiatedQuestSection),
  }
}

const sortQuestSections = (questSections: {
  [key: string]: QuestInstance[][]
}) => {
  const order: { [key: string]: number } = {
    morning: 0,
    afternoon: 1,
    night: 2,
    anytime: 3,
  }
  const result = Object.entries(questSections).sort(
    ([keyA], [keyB]) => order[keyA] - order[keyB]
  )
  return result
}
